import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDate, getStates } from "../../services/helpers";
import { HttpService } from "../../services/httpService";
import ToolTip from "../../components/component-tools/ToolTip";
import { ReferenceDetail } from "../../interfaces/referenceDetaill";
import { ReferenceOrder } from "../../interfaces/referenceOrder";
import {
  createReferenceDetail,
  deleteReferenceDetail,
  deleteReferenceDetailResponse,
  GetAllGeneralReferenceDocuments,
  getReferenceDetailByID,
  updateReferenceDetail,
  uploadReceivedForm,
} from "../../services/referenceDetailService";
import { getReferenceOrderByID } from "../../services/referenceOrderService";
import { ReferenceLetter } from "../../interfaces/referenceLetter";
import {
  getReferenceLettersByReferenceDetailID,
  updateReferenceLetters,
} from "../../services/referenceLetterService";
import ordinal from "ordinal";
import NotifyClient from "../../components/order-components/NotifyClient";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import FillDetailPopup from "../../components/order-components/FillDetail";
import { Bishop } from "../../interfaces/bishop";
import { School } from "../../interfaces/school";
import UploadGeneralReferenceDoc from "../../components/order-components/UploadGeneralReferenceDoc";
import DownloadGeneralReferenceDocs from "../../components/order-components/DownloadGeneralReferenceDocs";
import SendLetter from "../../components/order-components/SendLetter";
import { useViewOrderContext } from "../../contexts/ViewOrderContext";
import { SimpleOrder } from "../../interfaces/simpleOrder";
import { getSimpleOrderByID } from "../../services/orderService";
import { NotificationType } from "../../interfaces/clientNotification";

const AddReference = () => {
  const [referenceDetail, setReferenceDetail] =
    useState<ReferenceDetail | null>(null);
  const [referenceOrder, setReferenceOrder] = useState<ReferenceOrder | null>(
    null,
  );
  const [order, setOrder] = useState<SimpleOrder | null>(null);
  const { state } = useLocation();
  var referenceOrderID = state?.referenceOrderID || null;
  const referenceDetailID = state?.referenceDetailID || null;
  const [file, setFile] = useState<File | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [receivedFormUrl, setReceivedFormUrl] = useState<string | null>(null);
  const navigate = useNavigate();
  const [allLetters, setAllLetters] = useState<ReferenceLetter[]>();
  const states = getStates();
  const [showNotifyClientPopup, setShowNotifyClientPopup] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showAddBishopPopup, setShowAddBishopPopup] = useState(false);
  const [showAddSchoolPopup, setShowAddSchoolPopup] = useState(false);
  const [
    showUploadGeneralReferenceDocPopup,
    setShowUploadGeneralReferenceDocPopup,
  ] = useState(false);
  const [
    showDownloadGeneralReferenceDocsPopup,
    setShowDownloadGeneralReferenceDocsPopup,
  ] = useState(false);
  const [showSendLetterModal, setShowSendLetterModal] = useState(false);
  const { setReferenceDetails, referenceDetails } = useViewOrderContext();
  const [referenceDocCount, setReferenceDocCount] = useState(0);
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [
    showDeleteReferenceResponseConfirmation,
    setShowDeleteReferenceResponseConfirmation,
  ] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showBishopWarning, setShowBishopWarning] = useState(false);

  const statusClassMap: Record<number, string> = {
    1: "status-pending",
    2: "status-admin-review",
    3: "status-in-process",
    4: "status-completed",
    5: "status-deleted",
    6: "status-subscription",
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchReferenceOrderDetails = async () => {
      try {
        if (referenceDetailID) {
          const id = parseInt(referenceDetailID, 10);
          const response = await getReferenceDetailByID(id);
          setReferenceDetail(response);

          referenceOrderID = response.referenceOrderID;

          //get received form url
          try {
            if (response?.completedFormUrl) {
              const signedReferenceUrl = await HttpService.getFileAdmin(
                "reference-detail-forms",
                response.referenceDetailID.toString(),
                response.completedFormUrl,
              );
              setReceivedFormUrl(signedReferenceUrl);
            }
          } catch (error) {
            console.error(
              `Failed to fetch signed URL for received form`,
              error,
            );
          }

          //get reference letters
          const lettersResponse = await getReferenceLettersByReferenceDetailID(
            response.referenceDetailID,
          );
          setAllLetters(lettersResponse);

          const data = await GetAllGeneralReferenceDocuments(id);
          setReferenceDocCount(data.length);
        } else {
          setReferenceDetail({
            referenceDetailID: 0,
            referenceOrderID: referenceOrderID,
            letterReturned: undefined,
            redFlag: false,
            placeName: "",
            referenceName: "",
            referenceEmail: "",
            referencePhone: "",
            referenceAddress: "",
            referenceAddress2: "",
            referenceCity: "",
            referenceState: "",
            referenceZipCode: "",
            referenceCountry: "",
            fromDate: undefined,
            toDate: undefined,
            comments: "",
            completedFormUrl: "",
            clientNotifiedDate: undefined,
            adminReviewed: false,
            retract: false,
            followUp: true,
            sendCertified: true,
            mailAddrDifferent: false,
            confidential: false,
            candidatePosition: "",
            requesting: false,
            downloadChecked: false,
          });
        }
      } catch (err) {
        console.log("Failed to fetch reference detail.");
      } finally {
        setLoadingSpinner(false);
      }

      // get reference order
      try {
        const referenceOrderResponse =
          await getReferenceOrderByID(referenceOrderID);
        setReferenceOrder(referenceOrderResponse);

        const orderResponse = await getSimpleOrderByID(
          referenceOrderResponse.orderID,
        );
        setOrder(orderResponse);
      } catch {
        console.log("Reference Order ID not found.");
      }
    };

    fetchReferenceOrderDetails();
  }, [referenceDetailID, referenceOrderID]);

  const updateDocCount = async () => {
    if (!referenceDetail?.referenceDetailID) return;
    const data = await GetAllGeneralReferenceDocuments(
      referenceDetail?.referenceDetailID,
    );
    setReferenceDocCount(data.length);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
    letter?: ReferenceLetter,
  ) => {
    setEditing(true);
    let value: string | boolean =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (referenceDetail && field in referenceDetail) {
      setReferenceDetail({
        ...referenceDetail,
        [field]: value,
      });
    }

    if (letter) {
      setAllLetters((prev) =>
        (prev ?? []).map((l) =>
          l.referenceLetterID === letter.referenceLetterID
            ? { ...l, [field]: value }
            : l,
        ),
      );
    }
  };

  const handleFormUpload = async () => {
    if (referenceDetail?.referenceDetailID) {
      //save right away
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "image/*,.pdf";
      fileInput.click();
      fileInput.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
          setLoadingSpinner(true);
          const response = await uploadReceivedForm(
            file,
            referenceDetail.referenceDetailID,
          );
          setSuccessMessage("Received form uploaded successfully");
          setReceivedFormUrl(response.receivedFormUrl);
          if (response.receivedFormUrl) {
            const signedReferenceUrl = await HttpService.getFileAdmin(
              "reference-detail-forms",
              response.referenceDetailID.toString(),
              response.receivedFormUrl,
            );
            setReceivedFormUrl(signedReferenceUrl);
          }
          window.location.reload();
        } catch (error) {
          console.error("Error handling file upload:", error);
        } finally {
          setLoadingSpinner(false);
        }
      };
    } else {
      //store til final save
      try {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = "image/*,.pdf";
        fileInput.click();
        fileInput.onchange = async (event: any) => {
          const file = event.target.files[0];
          if (!file) return;
          setFile(file);
          setSuccessMessage("Received form uploaded successfully");
        };
        setEditing(true);
      } catch (error) {
        console.error("Error handling file upload:", error);
      } finally {
        setLoadingSpinner(false);
      }
    }
  };

  const handleSave = async () => {
    setLoadingSpinner(true);

    try {
      if (
        referenceDetail &&
        referenceDetail.referenceDetailID === 0 &&
        referenceOrder
      ) {
        if (referenceDetail.referenceState !== "INT") {
          referenceDetail.referenceCountry = "United States";
        }
        // new reference detail and possibly response file
        const result = await createReferenceDetail(referenceDetail);
        setReferenceDetail(result);

        setReferenceDetails([...referenceDetails, result]);

        if (file) {
          await uploadReceivedForm(file, result.referenceDetailID);
        }
        navigate(`/admin/view-order/${referenceOrder.orderID}`, {
          state: {
            successMessage: "Reference Detail created successfully.",
          },
        });
      } else if (referenceDetail && referenceDetail.referenceDetailID !== 0) {
        // update reference detail

        // set received date correctly after clear
        const payload: any = { ...referenceDetail };
        if (payload.letterReturned === "") {
          payload.letterReturned = null;
        }
        if (payload.fromDate === "") {
          payload.fromDate = null;
        }
        if (payload.toDate === "") {
          payload.toDate = null;
        }

        const result = await updateReferenceDetail(payload as ReferenceDetail);

        setReferenceDetail(result);

        setReferenceDetails((prev) =>
          prev.map((rd) =>
            rd.referenceDetailID === result.referenceDetailID ? result : rd,
          ),
        );

        if (allLetters && allLetters?.length > 0) {
          const lettersResult = await updateReferenceLetters(allLetters);
          setAllLetters(lettersResult);
        }

        setSuccessMessage("Updated Successfully.");
        setEditing(false);
      }
    } catch (error) {
      console.error("Error saving reference details:", error);
      alert("An error occurred while saving the form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (referenceDetailID && referenceDetail && referenceOrder) {
        await deleteReferenceDetail(referenceDetail.referenceDetailID);

        setReferenceDetails(
          referenceDetails.filter(
            (rd) => rd.referenceDetailID !== referenceDetail.referenceDetailID,
          ),
        );

        navigate(`/admin/view-order/${referenceOrder.orderID}`, {
          state: {
            successMessage: "Reference Detail deleted successfully.",
          },
        });
      }
    } catch (error) {
      console.error("Error deleting reference detail:", error);
      setSuccessMessage("");
    }
  };

  const handleNotifyClientClosePopup = (message?: string) => {
    if (message) {
      window.location.reload();
    }
    setShowNotifyClientPopup(false);
  };

  const handlePrefill = async (selectedItem?: Bishop | School) => {
    if (selectedItem && referenceDetail) {
      const isBishop = (item: Bishop | School): item is Bishop =>
        "bishopID" in item;
      const isSchool = (item: Bishop | School): item is School =>
        "schoolID" in item;

      const isSchoolBool = isSchool(selectedItem);
      const isBishopBool = isBishop(selectedItem);

      const toLog = isBishopBool ? selectedItem.bishopName : "";
      const toLog2 = order?.clientAbbreviation;

      console.log(toLog);
      console.log(toLog2);

      const requesting =
        isBishopBool && selectedItem.bishopName === order?.clientAbbreviation
          ? true
          : false;

      setReferenceDetail({
        ...referenceDetail,
        placeName: isSchoolBool
          ? selectedItem.name
          : isBishopBool
            ? selectedItem.diocese
            : "",
        referenceName: selectedItem.contact,
        referenceEmail: selectedItem.email,
        referencePhone: selectedItem.phone,
        referenceAddress: selectedItem.address,
        referenceAddress2: selectedItem.address2,
        referenceCity: selectedItem.city,
        referenceState: selectedItem.state,
        referenceZipCode: selectedItem.zipCode,
        comments: selectedItem.comments,
        sendCertified: selectedItem.sendCertified ?? true,
        requesting: requesting,
      });

      setShowBishopWarning(requesting);
    }

    setShowAddBishopPopup(false);
    setShowAddSchoolPopup(false);
    setEditing(true);
  };

  const handleDeleteReferenceResponse = async () => {
    try {
      setLoadingSpinner(true);
      if (file && !receivedFormUrl) {
        setFile(null);
        return;
      }

      if (!referenceDetail) {
        return;
      }

      await deleteReferenceDetailResponse(referenceDetail.referenceDetailID);

      window.location.reload();
    } catch (error) {
      console.error("Error deleting reference response:", error);
    } finally {
      setLoadingSpinner(false);
      setShowDeleteReferenceResponseConfirmation(false);
    }
  };

  const handleOpenProtocolPdf = async () => {
    window.open(
      "https://oxfordstoreacctprd.blob.core.windows.net/documents/Protocol.pdf",
      "_blank",
    );
  };

  return (
    <div className="content-container">
      <div className="data-container">
        <div
          className="page-items-container"
          style={{
            alignItems: "center",
            flex: "0 0 100%",
            justifyContent: "space-between",
          }}
        >
          <h1>
            {referenceDetailID
              ? "View Reference Service"
              : "Create Reference Service"}
          </h1>
          {order !== null && (
            <div
              className="page-items-container"
              style={{ alignItems: "center" }}
            >
              <h3
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/admin/view-order/${order.orderID}`)}
              >
                {order?.candidateFirstName + " " + order?.candidateLastName}
              </h3>
              <div
                className={`chip-date ${
                  statusClassMap[order?.orderStatusID ?? 1] || "status-unknown"
                }`}
                style={{
                  gap: "10px",
                  justifyContent: "end",
                }}
              >
                <div>
                  {order.orderStatusID === 2
                    ? "Admin Review"
                    : order.orderStatusID === 3
                      ? "In Process"
                      : order.orderStatusID === 4
                        ? "Completed"
                        : order.orderStatusID === 5
                          ? "Deleted"
                          : order.orderStatusID === 6
                            ? "Subscription"
                            : "Pending"}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="divider-small" style={{ margin: "0px" }}></div>
        {/* Letter tracking */}
        {referenceDetailID && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div className="text-sub-sub-header">Letter Tracking</div>
                <div style={{ flex: "0 0 50%" }}>
                  <div className="field-long" style={{ flex: "0 0 100%" }}>
                    <div className="field-label">
                      <ToolTip
                        tooltipText={`${referenceDetail?.alert}\n${formatDate(referenceDetail?.alertLastUpdatedDate, "datetime")}`}
                        placement="top"
                        className="alert-sticky"
                      >
                        <i className="bi bi-stickies-fill"></i>
                      </ToolTip>
                    </div>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="text"
                        value={referenceDetail?.alert ?? ""}
                        onChange={(e) => handleInputChange(e, "alert")}
                        maxLength={4000}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "20px",
                      fontSize: "14px",
                      marginTop: "4px",
                      justifyContent: "right",
                    }}
                  >
                    <div>
                      {formatDate(
                        referenceDetail?.alertLastUpdatedDate,
                        "datetime",
                      )}
                    </div>
                    <div>{referenceDetail?.alert?.length ?? 0}/4000</div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                {allLetters?.length !== 0 ? (
                  allLetters?.map((letter, index) => (
                    <div
                      key={letter.referenceLetterID}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div className="field-long" style={{ flex: "0 0 25%" }}>
                        <label className="field-label">
                          {`${ordinal(index + 1)} Contact`}:
                        </label>
                        <div className="field-input">
                          <input
                            className="form-control"
                            type="text"
                            value={formatDate(letter.letterDate, "us")}
                            disabled
                            readOnly
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "5px",
                          flexWrap: "wrap",
                          flex: "0 0 75%",
                        }}
                      >
                        <div className="field-long" style={{ flex: "0 0 10%" }}>
                          <div style={{ display: "flex", gap: "5px" }}>
                            <label className="field-label">Ignore: </label>
                            <ToolTip
                              tooltipText="Ignoring this means the contact date won’t count toward reference tracking."
                              placement="right"
                              className="info-icon-spec"
                            ></ToolTip>
                          </div>
                          <div>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={letter.ignore ? letter.ignore : false}
                              onChange={(e) =>
                                handleInputChange(e, "ignore", letter)
                              }
                              disabled={!referenceDetailID}
                            />
                          </div>
                        </div>
                        <div style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}>
                          <b>{letter.address}</b>
                        </div>
                        {letter.sentByMail && (
                          <div
                            style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
                          >
                            (Sent By Mail)
                          </div>
                        )}
                        {letter.certified && (
                          <div
                            style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
                          >
                            (Certified)
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div>no letters sent.</div>
                )}
              </div>
            </div>
            <div style={{ flex: "0 0 100%" }}> </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                <div style={{ display: "flex", gap: "5px" }}>
                  <label className="field-label">Letter Returned: </label>
                  <ToolTip
                    tooltipText="When the reference questionnaire is returned by the reference."
                    placement="right"
                    className="info-icon-spec"
                  ></ToolTip>
                </div>
                <div className="field-input">
                  <input
                    className="form-control"
                    type="date"
                    value={
                      referenceDetail?.letterReturned !== undefined
                        ? formatDate(referenceDetail?.letterReturned)
                        : ""
                    }
                    onChange={(e) => handleInputChange(e, "letterReturned")}
                    disabled={!referenceDetailID}
                  />
                </div>
              </div>

              <button
                className={`smbtn btn-grey ${
                  !referenceDetailID || referenceDetail?.confidential
                    ? "disabled"
                    : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSendLetterModal(true);
                }}
                disabled={!referenceDetailID || referenceDetail?.confidential}
              >
                Send Reference Letter <i className="btn-icon bi bi-send"></i>
              </button>
            </div>
            <div style={{ flex: "0 0 100%" }}></div>
            <div style={{ flex: "0 0 100%" }}></div>
          </>
        )}

        {showBishopWarning && (
          <div className="error">
            Requesting – confirm candidate exceeds 3 months
          </div>
        )}

        <div className="field-long">
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">Requesting: </label>
            <ToolTip
              tooltipText="Confirm candidate exceeds 3 months"
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </div>
          <div>
            <input
              className="form-check-input check-red"
              type="checkbox"
              checked={
                referenceDetail?.requesting
                  ? referenceDetail?.requesting
                  : false
              }
              onChange={(e) => handleInputChange(e, "requesting")}
              disabled={!referenceDetailID}
            />
          </div>
        </div>

        {showSendLetterModal === true && (
          <SendLetter
            referenceDetailID={
              referenceDetail?.referenceDetailID
                ? referenceDetail.referenceDetailID
                : 0
            }
            onClose={() => {
              setShowSendLetterModal(false);
            }}
            onSuccess={() => window.location.reload()}
          />
        )}

        {/* Prefill Data */}
        <div
          style={{
            flex: "0 0 100%",
            display: "flex",
            gap: "20px",
            alignItems: "start",
          }}
        >
          <button
            className="smbtn btn-purple"
            onClick={() => setShowAddSchoolPopup(true)}
          >
            Add School
            <i className="bi bi-plus-lg btn-icon"></i>
          </button>
          <div style={{ display: "flex", gap: "5px" }}>
            <button
              className="smbtn btn-purple"
              onClick={() => setShowAddBishopPopup(true)}
            >
              Add Bishop
              <i className="bi bi-plus-lg btn-icon"></i>
            </button>
            <ToolTip
              tooltipText="For Episcopal/Anglican dioceses, use the lookup. For others, confirm
            the current bishop’s contact via Google and disregard past bishops."
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </div>

          {showAddBishopPopup && (
            <FillDetailPopup type={"bishop"} onClose={handlePrefill} />
          )}

          {showAddSchoolPopup && (
            <FillDetailPopup type={"school"} onClose={handlePrefill} />
          )}
        </div>

        <div className="field-stacked" style={{ flex: "0 0 45%" }}>
          <label className="field-label">Entity Name:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={referenceDetail?.placeName || ""}
              onChange={(e) => handleInputChange(e, "placeName")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 45%" }}>
          <div style={{ display: "flex", gap: "5px", cursor: "pointer" }}>
            <label className="field-label">Contact Name:</label>
            <div onClick={() => handleOpenProtocolPdf()}>
              <ToolTip
                tooltipText="Click to open protocol pdf."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>
          </div>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={referenceDetail?.referenceName || ""}
              onChange={(e) => handleInputChange(e, "referenceName")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 40%" }}>
          <label className="field-label">Contact Email:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={referenceDetail?.referenceEmail || ""}
              onChange={(e) => handleInputChange(e, "referenceEmail")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">Contact Phone:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={referenceDetail?.referencePhone || ""}
              onChange={(e) => handleInputChange(e, "referencePhone")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}></div>
        <div className="field-stacked" style={{ flex: "0 0 25%" }}>
          <label className="field-label">Address 1:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={referenceDetail?.referenceAddress || ""}
              onChange={(e) => handleInputChange(e, "referenceAddress")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">Address 2:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={referenceDetail?.referenceAddress2 || ""}
              onChange={(e) => handleInputChange(e, "referenceAddress2")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">City:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={referenceDetail?.referenceCity || ""}
              onChange={(e) => handleInputChange(e, "referenceCity")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">State:</label>
          <select
            className={`form-select`}
            value={referenceDetail?.referenceState}
            onChange={(e) => handleInputChange(e, "referenceState")}
            // disabled={closed}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.abbreviation} value={state.abbreviation}>
                {state.name} ({state.abbreviation})
              </option>
            ))}
          </select>
        </div>
        {referenceDetail?.referenceState === "INT" && (
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Country:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={
                  referenceDetail.referenceCountry
                    ? referenceDetail.referenceCountry
                    : ""
                }
                onChange={(e) => handleInputChange(e, "referenceCountry")}
                // disabled={closed}
              />
            </div>
          </div>
        )}
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">Zip Code:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={referenceDetail?.referenceZipCode || ""}
              onChange={(e) => handleInputChange(e, "referenceZipCode")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">From: </label>
          <div className="field-input">
            <input
              className="form-control"
              type="date"
              value={
                referenceDetail?.fromDate !== undefined
                  ? formatDate(referenceDetail?.fromDate)
                  : ""
              }
              onChange={(e) => handleInputChange(e, "fromDate")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">To: </label>
          <div className="field-input">
            <input
              className="form-control"
              type="date"
              value={
                referenceDetail?.toDate !== undefined
                  ? formatDate(referenceDetail?.toDate)
                  : ""
              }
              onChange={(e) => handleInputChange(e, "toDate")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 40%" }}>
          <label className="field-label">Candidate Position:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={referenceDetail?.candidatePosition || ""}
              onChange={(e) => handleInputChange(e, "candidatePosition")}
              // disabled={closed}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <div style={{ display: "flex", gap: "5px" }}>
            <label className="field-label">Confidential Contact:</label>
            <ToolTip
              tooltipText="Candidate has requested that no contact be made with this reference."
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </div>
          <div>
            <input
              className="form-check-input check-yellow"
              type="checkbox"
              checked={
                referenceDetail?.confidential
                  ? referenceDetail?.confidential
                  : false
              }
              onChange={(e) => handleInputChange(e, "confidential")}
              disabled={!referenceDetailID}
            />
          </div>
        </div>

        <div style={{ flex: "0 0 100%" }}></div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div className="field-stacked">
                <label className="field-label">Red Flag:</label>
                <div>
                  <input
                    className="form-check-input check-red"
                    type="checkbox"
                    checked={
                      referenceDetail?.redFlag
                        ? referenceDetail?.redFlag
                        : false
                    }
                    onChange={(e) => handleInputChange(e, "redFlag")}
                    disabled={!referenceDetailID}
                  />
                </div>
              </div>
              <div className="field-stacked">
                <div style={{ display: "flex", gap: "5px" }}>
                  <label className="field-label">Admin Reviewed:</label>
                  <ToolTip
                    tooltipText="Reference response is visible to the client in their portal. Check after reference response is received"
                    placement="right"
                    className="info-icon-spec"
                  ></ToolTip>
                </div>
                <div>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={
                      referenceDetail?.adminReviewed
                        ? referenceDetail?.adminReviewed
                        : false
                    }
                    onChange={(e) => handleInputChange(e, "adminReviewed")}
                    disabled={!referenceDetailID}
                  />
                </div>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "end",
                gap: "10px",
              }}
            >
              {(receivedFormUrl || file) && (
                <div style={{ display: "flex", gap: "5px" }}>
                  <button
                    className={`smbtn btn-grey ${
                      !referenceDetailID || referenceDetail?.clientNotifiedDate
                        ? "disabled"
                        : ""
                    }`}
                    onClick={() =>
                      setShowNotifyClientPopup(!showNotifyClientPopup)
                    }
                    disabled={
                      !referenceDetailID ||
                      referenceDetail?.clientNotifiedDate !== null
                    }
                  >
                    Notify Client <i className="btn-icon bi bi-envelope"></i>
                  </button>
                  <ToolTip
                    tooltipText="Notify the client of a “Yes” or comment response, if notified, the reference will appear on the final report."
                    placement="bottom"
                    className="info-icon-spec"
                  />
                </div>
              )}
              {referenceDetail?.clientNotifiedDate && (
                <>
                  <div
                    className="field-long"
                    style={{ flex: "0 0 20%", alignItems: "end" }}
                  >
                    <label className="field-label">Client Notified: </label>
                    <div className="field-input">
                      <input
                        className="form-control"
                        type="date"
                        value={
                          referenceDetail?.clientNotifiedDate !== undefined
                            ? formatDate(referenceDetail?.clientNotifiedDate)
                            : ""
                        }
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="field-long" style={{ flex: "0 0 20%" }}>
                    <div style={{ display: "flex", gap: "5px" }}>
                      <label className="field-label">Retract:</label>
                      <ToolTip
                        tooltipText="Even though client was notified, don't include this reference on the final report."
                        placement="right"
                        className="info-icon-spec"
                      ></ToolTip>
                    </div>
                    <div>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={
                          referenceDetail?.retract
                            ? referenceDetail?.retract
                            : false
                        }
                        onChange={(e) => handleInputChange(e, "retract")}
                        disabled={!referenceDetailID}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div>
            <div className="field-stacked" style={{ flex: "0 0 100%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Visible:</label>
                <ToolTip
                  tooltipText="Manages visibility settings for the reference tracking table"
                  placement="left"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={
                    referenceDetail?.followUp
                      ? referenceDetail?.followUp
                      : false
                  }
                  onChange={(e) => handleInputChange(e, "followUp")}
                  disabled={!referenceDetailID}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 100%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Send Certified:</label>
                <ToolTip
                  tooltipText="Controls if csv box is visible on the reference tracking page."
                  placement="left"
                  className="info-icon-spec"
                ></ToolTip>
              </div>
              <div>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={
                    referenceDetail?.sendCertified
                      ? referenceDetail?.sendCertified
                      : false
                  }
                  onChange={(e) => handleInputChange(e, "sendCertified")}
                  disabled={!referenceDetailID}
                />
              </div>
            </div>
          </div>
        </div>

        {showNotifyClientPopup && referenceDetail && order && (
          <NotifyClient
            orderId={order?.orderID}
            clientId={order.clientID}
            detailId={referenceDetail.referenceDetailID}
            candidateEmail={order.candidateEmail}
            formName={referenceDetail.completedFormUrl ?? ""}
            type={NotificationType.Reference}
            onClose={handleNotifyClientClosePopup}
          />
        )}

        <div className="field-stacked" style={{ flex: "0 0 100%" }}>
          <label className="field-label">Comments:</label>
          <textarea
            className="form-control"
            value={referenceDetail?.comments || ""}
            onChange={(e) => handleInputChange(e, "comments")}
            // disabled={closed}
            style={{ height: "100px" }}
            maxLength={250}
          />
          <div
            style={{
              fontSize: "14px",
              marginTop: "4px",
              textAlign: "right",
            }}
          >
            {referenceDetail?.comments?.length ?? 0}/250
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "end",
            }}
          >
            {receivedFormUrl && (
              <div
                style={{
                  display: "flex",
                }}
              >
                <button
                  className="smbtn btn-purple"
                  onClick={() => {
                    if (receivedFormUrl) {
                      window.open(receivedFormUrl, "_blank");
                    }
                  }}
                >
                  View Reference Response
                  <i className="btn-icon bi bi-box-arrow-up-right"></i>
                </button>
                <div
                  onClick={() =>
                    setShowDeleteReferenceResponseConfirmation(true)
                  }
                >
                  <i
                    className="bi bi-x"
                    style={{ color: "red", cursor: "pointer" }}
                  ></i>
                </div>
              </div>
            )}
            {file && (
              <>
                <div>
                  <button className="smbtn btn-purple">
                    <a
                      href={URL.createObjectURL(file)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link"
                      style={{ color: "white" }}
                    >
                      View Reference Response
                      <i className="btn-icon bi bi-box-arrow-up-right"></i>
                    </a>
                  </button>
                  <div
                    onClick={() =>
                      setShowDeleteReferenceResponseConfirmation(true)
                    }
                  >
                    <i
                      className="bi bi-x"
                      style={{ color: "red", cursor: "pointer" }}
                    ></i>
                  </div>
                </div>
              </>
            )}
            {showDeleteReferenceResponseConfirmation && (
              <ConfirmDelete
                onConfirm={() => handleDeleteReferenceResponse()}
                onCancel={() => {
                  setShowDeleteReferenceResponseConfirmation(false);
                }}
                message="Delete this Reference Response?"
              />
            )}
            {referenceDetailID && (
              <button
                className="smbtn btn-grey"
                onClick={() => setShowUploadConfirmation(true)}
                style={{ marginLeft: "20px" }}
              >
                Upload Response
              </button>
            )}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <div>
              <button
                style={{ marginLeft: "20px" }}
                className={`smbtn btn-grey`}
                onClick={() => setShowUploadGeneralReferenceDocPopup(true)}
              >
                General Document Upload
              </button>
            </div>

            <div style={{ display: "flex" }}>
              <button
                style={{ marginLeft: "20px" }}
                className={`smbtn btn-grey`}
                onClick={() => setShowDownloadGeneralReferenceDocsPopup(true)}
              >
                Download Document
              </button>
              {referenceDocCount > 0 && (
                <div style={{ display: "flex", alignSelf: "start" }}>
                  <span className="doc-count-dot">{referenceDocCount}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {showUploadConfirmation && (
          <ConfirmDelete
            onConfirm={() => handleFormUpload()}
            onCancel={() => {
              setShowUploadConfirmation(false);
            }}
            message="If there is a form uploaded, you will be replacing it."
          />
        )}

        {showUploadGeneralReferenceDocPopup && referenceDetail && (
          <UploadGeneralReferenceDoc
            referenceDetail={referenceDetail}
            onClose={() => {
              setShowUploadGeneralReferenceDocPopup(false);
              updateDocCount();
            }}
          />
        )}
        {showDownloadGeneralReferenceDocsPopup && referenceDetail && (
          <DownloadGeneralReferenceDocs
            referenceDetail={referenceDetail}
            onClose={() => {
              setShowDownloadGeneralReferenceDocsPopup(false);
              updateDocCount();
            }}
          />
        )}

        <div style={{ flex: "0 0 100%" }}>
          {successMessage && (
            <div className=" alert alert-success fade show">
              {successMessage}
            </div>
          )}
        </div>
      </div>
      <div className="action-buttons">
        <button
          className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
          onClick={() => handleSave()}
          disabled={!editing}
        >
          Save
        </button>
        {referenceDetailID && (
          <button
            className="oxbtn btn-red"
            onClick={() => setShowConfirmation(true)}
          >
            Delete
          </button>
        )}
        {showConfirmation && (
          <ConfirmDelete
            onConfirm={handleDelete}
            onCancel={() => {
              setShowConfirmation(false);
            }}
            message="Are you sure you want to delete this reference?"
          />
        )}
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default AddReference;
